import { useState, useEffect, useCallback, useRef } from 'react'
import { Ban, AlertTriangle, Loader2, Calendar, School, Landmark, AlertCircle, User, FileText, ArrowLeft } from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { studentsApi } from '@/services/api'
import { useNavigate } from 'react-router-dom'

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE — synchronisées avec DashboardHeader
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

// ─── Helpers de récupération localStorage ───
const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY)
    if (sid) return Number(sid)
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e) }
  return null
}

const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yid) return Number(yid)
  } catch (e) { console.warn('getStoredYearId error:', e) }
  return null
}

const getStoredYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) } catch (e) { return null }
}

interface ExpelledStudent {
  id: number
  matricule: string
  first_name: string
  last_name: string
  sex: string
  date_of_birth: string
  class_name?: string
  school_name?: string
  status: string
  expelled_at?: string
  expulsion_reason?: string
  expulsion_decision?: string
  academic_year_id?: number
  school_id?: number
}

export default function StudentExpelledPage() {
  const { lang } = useLang()
  const navigate = useNavigate()

  // ─── RÉCUPÉRATION DEPUIS LOCALSTORAGE ───
  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId)
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId)
  const [academicYearName, setAcademicYearName] = useState<string>(getStoredYearLabel() || '')

  const [students, setStudents] = useState<ExpelledStudent[]>([])
  const [loading, setLoading] = useState(true)

  // ─── REFS pour éviter les boucles infinies ───
  const schoolIdRef = useRef<number | null>(schoolId)
  const yearIdRef = useRef<number | null>(academicYearId)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = academicYearId }, [academicYearId])

  const $t = {
    fr: {
      title: 'Élèves renvoyés',
      subtitle: 'Liste des élèves exclus définitivement',
      loading: 'Chargement...',
      error: 'Erreur de chargement',
      noData: 'Aucun élève renvoyé',
      noDataDesc: "Aucun élève avec le statut \"renvoyé\" n'a été trouvé pour cette année scolaire.",
      school: 'Établissement',
      academicYear: 'Année scolaire',
      noYear: 'Aucune année sélectionnée',
      noSchool: 'Aucun établissement sélectionné',
      back: 'Retour',
      matricule: 'Matricule',
      name: 'Nom complet',
      sex: 'Sexe',
      birthDate: 'Date de naissance',
      class: 'Classe',
      expelledAt: "Date d'exclusion",
      reason: 'Motif',
      decision: 'Décision',
      registry: 'Registre des exclusions',
      registryDesc: 'Liste des élèves ayant été définitivement exclus avec les motifs, dates et décisions du conseil de discipline.',
    },
    en: {
      title: 'Expelled Students',
      subtitle: 'List of permanently excluded students',
      loading: 'Loading...',
      error: 'Loading error',
      noData: 'No expelled students',
      noDataDesc: 'No students with "expelled" status were found for this academic year.',
      school: 'School',
      academicYear: 'Academic Year',
      noYear: 'No year selected',
      noSchool: 'No school selected',
      back: 'Back',
      matricule: 'Reg. No.',
      name: 'Full Name',
      sex: 'Sex',
      birthDate: 'Date of Birth',
      class: 'Class',
      expelledAt: 'Exclusion Date',
      reason: 'Reason',
      decision: 'Decision',
      registry: 'Exclusion Registry',
      registryDesc: 'List of permanently excluded students with reasons, dates, and discipline council decisions.',
    }
  }[lang]

  // ─── CHARGEMENT DES DONNÉES — exécuté UNE SEULE FOIS au montage ───
  const loadExpelled = useCallback(async () => {
    setLoading(true)
    try {
      const currentSchoolId = schoolIdRef.current
      const currentYearId = yearIdRef.current

      const params: Record<string, any> = { status: 'renvoye' }
      if (currentSchoolId) params.school_id = currentSchoolId
      if (currentYearId) params.academic_year_id = currentYearId

      const res = await studentsApi.getAll(params)
      const items = res?.items || res?.data || res || []
      setStudents(Array.isArray(items) ? items : [])
    } catch (err) {
      console.error('Erreur chargement élèves renvoyés:', err)
      toast.error($t.error)
    } finally {
      setLoading(false)
    }
  }, [$t.error])

  useEffect(() => { loadExpelled() }, [loadExpelled])

  // ─── ÉCOUTE DES CHANGEMENTS D'ANNÉE (CustomEvent + localStorage) ───
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null
      const currentYearId = yearIdRef.current
      const currentSchoolId = schoolIdRef.current

      let changed = false

      if (newYearId && newYearId !== currentYearId) {
        setAcademicYearId(newYearId)
        yearIdRef.current = newYearId
        const label = detail.label || getStoredYearLabel() || ''
        setAcademicYearName(label)
        changed = true
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== currentSchoolId) {
        setSchoolId(newSchoolId)
        schoolIdRef.current = newSchoolId
        changed = true
      }

      if (changed) {
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading')
        loadExpelled()
      }
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      let changed = false

      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        const currentYearId = yearIdRef.current
        if (newYearId && newYearId !== currentYearId) {
          setAcademicYearId(newYearId)
          yearIdRef.current = newYearId
          const label = localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || ''
          setAcademicYearName(label)
          changed = true
        }
      }
      if (e.key === SELECTED_SCHOOL_ID_KEY) {
        const newSchoolId = e.newValue ? Number(e.newValue) : null
        const currentSchoolId = schoolIdRef.current
        if (newSchoolId && newSchoolId !== currentSchoolId) {
          setSchoolId(newSchoolId)
          schoolIdRef.current = newSchoolId
          changed = true
        }
      }

      if (changed) {
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading')
        loadExpelled()
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [lang, loadExpelled])

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            {$t.title}
          </h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-red-600 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* ═══════════════════════ HEADER ═══════════════════════ */}
      <div className="page-header">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              {$t.title}
            </h1>
            <p className="page-subtitle">{$t.subtitle}</p>
            {/* ─── BADGE ÉTABLISSEMENT & ANNÉE ─── */}
            <div className="flex items-center gap-2 mt-1.5">
              {schoolId && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Landmark className="w-3 h-3" />
                  {$t.school} ID: {schoolId}
                </span>
              )}
              {academicYearId && academicYearName && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {academicYearName}
                </span>
              )}
              {!schoolId && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {$t.noSchool}
                </span>
              )}
              {schoolId && !academicYearId && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {$t.noYear}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/students')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            {$t.back}
          </button>
        </div>
      </div>

      {/* ═══════════════════════ CONTENU ═══════════════════════ */}
      {students.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-16">
            <Ban className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {$t.registry}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {$t.registryDesc}
            </p>
            <p className="text-gray-400 text-sm mt-4">
              {$t.noDataDesc}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.matricule}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.name}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.sex}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.birthDate}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.class}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.expelledAt}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.reason}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.decision}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {students.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-mono text-blue-700 font-medium">{student.matricule}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-700 text-xs font-bold">
                            {student.first_name[0]}{student.last_name[0]}
                          </div>
                          <span className="font-medium text-gray-900">{student.last_name} {student.first_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          student.sex === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                        }`}>
                          {student.sex === 'M' ? '♂' : '♀'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(student.date_of_birth)}</td>
                      <td className="px-4 py-3 text-gray-600">{student.class_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(student.expelled_at)}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 max-w-[200px] truncate block" title={student.expulsion_reason}>
                          {student.expulsion_reason || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          {student.expulsion_decision || 'Exclusion définitive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {students.length} {lang === 'fr' ? 'élève(s) renvoyé(s)' : 'expelled student(s)'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}